import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import RegisterLink from '@/components/ui/RegisterLink';

const NAV_LINKS = [
  { label: 'Promotions', href: 'https://bybid9.tips/RFLANDINGPAGESEO' },
  { label: 'RTP Games', href: 'https://bybid9.tips/RFLANDINGPAGESEO' },
  { label: 'Leaderboard', href: 'https://bybid9.tips/RFLANDINGPAGESEO' },
] as const;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setMenuOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled || menuOpen
          ? 'bg-[#0d1117]/95 backdrop-blur-xl border-b border-[#47CAF1]/15'
          : 'bg-transparent'
      }`}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 flex items-center justify-between min-h-14 sm:min-h-16 gap-2">
        <Link to="/" className="flex-shrink-0 py-2" onClick={() => setMenuOpen(false)}>
          <img
            src="https://xt30sf.b-cdn.net/media/21d9c82fb4d9675627365.gif"
            alt="BYBID9"
            className="h-9 sm:h-10 w-auto"
            style={{ filter: 'drop-shadow(0 0 10px rgba(71,202,241,0.5))' }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8 flex-wrap justify-end">
          {NAV_LINKS.map((item) => (
            <RegisterLink
              key={item.label}
              section="navbar"
              linkLabel={item.label}
              href={item.href}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium text-white/60 hover:text-[#47CAF1] transition-all duration-300 whitespace-nowrap relative group hover:drop-shadow-[0_0_8px_rgba(71,202,241,0.8)] py-2"
            >
              {item.label}
              <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-[#47CAF1] group-hover:w-full transition-all duration-300 rounded-full shadow-[0_0_6px_rgba(71,202,241,0.9)]" />
            </RegisterLink>
          ))}
          <Link
            to="/blog"
            className="text-sm font-medium text-white/60 hover:text-[#47CAF1] transition-all duration-300 whitespace-nowrap relative group hover:drop-shadow-[0_0_8px_rgba(71,202,241,0.8)] py-2"
          >
            Blog
            <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-[#47CAF1] group-hover:w-full transition-all duration-300 rounded-full shadow-[0_0_6px_rgba(71,202,241,0.9)]" />
          </Link>
        </nav>

        <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
          <RegisterLink
            section="navbar"
            linkLabel="Login"
            href="https://bybid9.tips/RFLANDINGPAGESEO"
            target="_blank"
            rel="noreferrer"
            className="btn-outline-premium text-sm px-5 py-2.5 min-h-[44px] inline-flex items-center justify-center"
          >
            Login
          </RegisterLink>
          <RegisterLink
            section="navbar"
            linkLabel="Register"
            href="https://bybid9.tips/RFLANDINGPAGESEO"
            target="_blank"
            rel="noreferrer"
            className="btn-premium text-sm px-6 py-2.5 min-h-[44px] inline-flex items-center justify-center"
          >
            <span>Register</span>
          </RegisterLink>
        </div>

        <button
          type="button"
          className="lg:hidden flex items-center justify-center w-11 h-11 min-w-[44px] min-h-[44px] rounded-xl border border-[#47CAF1]/25 text-[#47CAF1] bg-white/[0.04] active:bg-white/[0.08]"
          aria-expanded={menuOpen}
          aria-controls="mobile-nav"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} aria-hidden />
        </button>
      </div>

      {/* Portal: fixed inside header breaks under backdrop-filter (wrong containing block). */}
      {typeof document !== 'undefined' &&
        menuOpen &&
        createPortal(
          <div
            className="lg:hidden fixed inset-0 z-[200] flex flex-col pointer-events-auto"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
          >
            <button
              type="button"
              className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
              aria-label="Close menu"
              onClick={() => setMenuOpen(false)}
            />
            <div
              id="mobile-nav"
              className="relative mt-auto w-full max-h-[85vh] flex flex-col rounded-t-2xl border border-[#47CAF1]/20 border-b-0 bg-[#0a1219] shadow-[0_-12px_48px_rgba(0,0,0,0.55)] overflow-hidden"
              style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
            >
              <div className="flex justify-center pt-3 pb-2">
                <span className="h-1 w-10 rounded-full bg-white/20" aria-hidden />
              </div>
              <nav className="flex flex-col px-4 py-2 gap-0.5 overflow-y-auto" aria-label="Mobile">
                {NAV_LINKS.map((item) => (
                  <RegisterLink
                    key={item.label}
                    section="navbar_mobile"
                    linkLabel={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center min-h-12 px-3 rounded-xl text-base font-medium text-white/90 active:bg-white/[0.08]"
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </RegisterLink>
                ))}
                <Link
                  to="/blog"
                  className="flex items-center min-h-12 px-3 rounded-xl text-base font-medium text-white/90 active:bg-white/[0.08]"
                  onClick={() => setMenuOpen(false)}
                >
                  Blog
                </Link>
              </nav>
              <div className="px-4 pb-4 pt-2 flex flex-col gap-3 border-t border-[#47CAF1]/10 bg-[#070d14]/90">
                <RegisterLink
                  section="navbar_mobile"
                  linkLabel="Login"
                  href="https://bybid9.tips/RFLANDINGPAGESEO"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline-premium w-full text-center py-3.5 min-h-[48px] justify-center"
                  onClick={() => setMenuOpen(false)}
                >
                  Login
                </RegisterLink>
                <RegisterLink
                  section="navbar_mobile"
                  linkLabel="Register"
                  href="https://bybid9.tips/RFLANDINGPAGESEO"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-premium w-full text-center py-3.5 min-h-[48px] justify-center"
                  onClick={() => setMenuOpen(false)}
                >
                  <span>Register</span>
                </RegisterLink>
              </div>
            </div>
          </div>,
          document.body
        )}
    </header>
  );
}
